/**
 * @file     useLeaseValue.ts
 * @purpose  The rent read for the front door and the Pricing step: the
 *           asking band from active listed rentals for everyone signed in,
 *           and the leased band with comparables only when the route says
 *           the viewer is a VOW registrant. Refetches after a VOW unlock.
 */
'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { reportSilentFailure } from '@/core/observability/reportSilentFailure';
import { useVowUnlockToken } from '@/hooks/useVowUnlock';
import { isRentBand, type RentBand, type RentComparable } from '@/services/listing-submissions/rentRange';
import { ListingApiError, listingApi } from './api';

export type LeaseValueInput = {
  address?: string;
  latitude?: number | null;
  longitude?: number | null;
  bedrooms: number;
  homeType: string;
};

export type LeaseValueState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'signed_out' }
  | { status: 'not_found' }
  | { status: 'error' }
  | {
      status: 'ready';
      asking: RentBand | null;
      leased: RentBand | null;
      comparables: RentComparable[];
      leasedLocked: boolean;
    };

function body(input: LeaseValueInput) {
  return input.latitude != null && input.longitude != null
    ? { latitude: input.latitude, longitude: input.longitude, bedrooms: input.bedrooms, homeType: input.homeType }
    : { address: input.address ?? '', bedrooms: input.bedrooms, homeType: input.homeType };
}

/** Fetch on demand (front door) or whenever `auto` input changes (Pricing). */
export function useLeaseValue(auto: LeaseValueInput | null = null, fetcher: typeof listingApi.leaseValue = listingApi.leaseValue) {
  const [state, setState] = useState<LeaseValueState>({ status: 'idle' });
  const unlock = useVowUnlockToken();
  const seq = useRef(0);
  const last = useRef<LeaseValueInput | null>(null);

  const run = useCallback(
    async (input: LeaseValueInput): Promise<LeaseValueState> => {
      last.current = input;
      const id = ++seq.current;
      setState({ status: 'loading' });
      let next: LeaseValueState;
      try {
        const res = await fetcher(body(input));
        next = {
          status: 'ready',
          asking: isRentBand(res.asking) ? res.asking : null,
          leased: res.leased && isRentBand(res.leased) ? res.leased : null,
          comparables: res.comparables ?? [],
          leasedLocked: Boolean(res.leasedLocked),
        };
      } catch (err) {
        if (err instanceof ListingApiError && err.status === 401) next = { status: 'signed_out' };
        else if (err instanceof ListingApiError && err.details.includes('address:not_found')) next = { status: 'not_found' };
        else {
          reportSilentFailure(err, { area: 'owner-listings/lease-value' });
          next = { status: 'error' };
        }
      }
      if (id === seq.current) setState(next);
      return next;
    },
    [fetcher],
  );

  const key = auto ? JSON.stringify(body(auto)) : null;
  useEffect(() => {
    if (auto) void run(auto);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, run]);

  useEffect(() => {
    if (unlock > 0 && last.current) void run(last.current);
  }, [unlock, run]);

  return { state, run };
}
